import { useState, useEffect } from 'react';
import { Calendar, Clock, Shield, AlertTriangle, RefreshCw, CheckCircle, XCircle } from 'lucide-react';
import { fetchApi } from '../api/client';

export default function EarningsCalendar({ limit, onViewAll } = {}) {
  const [earnings, setEarnings] = useState(null);
  const [scorecards, setScorecards] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [earningsData, scorecardsData] = await Promise.all([
          fetchApi('/earnings/upcoming?days=30').catch(() => ({ upcoming: [] })),
          fetchApi('/scorecards?status=pending').catch(() => ({ scorecards: [] })),
        ]);
        setEarnings(earningsData.upcoming || []);
        
        // Build lookup: ticker -> has pending scorecard
        const scMap = {};
        (scorecardsData.scorecards || []).forEach(sc => {
          scMap[sc.ticker] = true;
        });
        setScorecards(scMap);
      } catch (err) {
        console.error('Earnings load error:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="card">
        <h3 className="card-header">
          <Calendar className="w-5 h-5 text-sky-400" />
          Upcoming Earnings
        </h3>
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-5 h-5 text-slate-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (!earnings || earnings.length === 0) {
    return (
      <div className="card">
        <h3 className="card-header">
          <Calendar className="w-5 h-5 text-sky-400" />
          Upcoming Earnings
        </h3>
        <p className="text-sm text-slate-400">No earnings in the next 30 days</p>
      </div>
    );
  }

  return (
    <div className="card">
      <h3 className="card-header">
        <Calendar className="w-5 h-5 text-sky-400" />
        Upcoming Earnings
      </h3>
      <div className="space-y-2">
        {(limit ? earnings.slice(0, limit) : earnings).map((e) => {
          const hasScorecard = scorecards?.[e.ticker];
          
          return (
            <div 
              key={e.ticker}
              className={`flex items-center justify-between p-2 rounded-lg border ${
                e.freeze_active 
                  ? 'bg-amber-500/5 border-amber-500/20' 
                  : 'bg-slate-800/30 border-slate-700/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-700/50 flex items-center justify-center text-sm font-bold text-sky-400">
                  {e.ticker.slice(0, 2)}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-200">{e.ticker}</span>
                    {e.freeze_active && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400 flex items-center gap-1">
                        <Shield className="w-3 h-3" />
                        FREEZE
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <span>{e.earnings_date}</span>
                    {e.earnings_time && (
                      <span className="flex items-center gap-0.5">
                        <Clock className="w-3 h-3" />
                        {e.earnings_time}
                      </span>
                    )}
                    {e.fiscal_quarter && (
                      <span className="text-slate-500">{e.fiscal_quarter}</span>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {/* Scorecard prep status */}
                <div className="flex items-center gap-1" title={hasScorecard ? 'Scorecard prepared' : 'No scorecard'}>
                  {hasScorecard ? (
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                  ) : (
                    <XCircle className="w-4 h-4 text-slate-500" />
                  )}
                </div>
                
                {/* Days countdown */}
                <div className={`text-right min-w-[3rem] ${
                  e.days_away <= 2 ? 'text-red-400' :
                  e.days_away <= 5 ? 'text-amber-400' :
                  'text-slate-400'
                }`}>
                  <span className="text-lg font-bold">{e.days_away}</span>
                  <span className="text-xs ml-0.5">d</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {limit && earnings.length > limit && onViewAll && (
        <button
          onClick={onViewAll}
          className="mt-3 text-sm text-sky-400 hover:text-sky-300 transition-colors"
        >
          View all {earnings.length} upcoming &rarr;
        </button>
      )}
    </div>
  );
}
